// GS graph API client — new gs_graph blueprint (routes/gs_graph.py).
// Lean by design: one read endpoint (/query), cached /meta, /trending.
// Phase 2 adds /state and /views. Doc previews reuse getMapDocPreview (mapApi).
import { AxiosClient } from "./AxiosClient";
import { BASE_URL } from "./config";

// body: { preset?: "trending", filters?: {...}, limit?, cursor? }
export const queryGraph = (body, config = {}) =>
  AxiosClient.post("v2/gs-graph/query", body, config);

export const getGraphMeta = () => AxiosClient.get("v2/gs-graph/meta");

export const getTrendingFilters = () => AxiosClient.get("v2/gs-graph/trending");

// Country outlines for the geo backdrop — static, cache aggressively client-side.
export const getWorldOutline = () => AxiosClient.get("v2/gs-graph/world");

export const getGraphState = () => AxiosClient.get("v2/gs-graph/state");

export const putGraphState = (state) =>
  AxiosClient.put("v2/gs-graph/state", { state });

export const getGraphViews = () => AxiosClient.get("v2/gs-graph/views");

export const createGraphView = (payload) =>
  AxiosClient.post("v2/gs-graph/views", payload);

export const updateGraphView = (id, payload) =>
  AxiosClient.put(`v2/gs-graph/views/${id}`, payload);

export const deleteGraphView = (id) =>
  AxiosClient.delete(`v2/gs-graph/views/${id}`);

export const createManualNode = (payload) =>
  AxiosClient.post("v2/gs-graph/nodes", payload);

export const createManualEdge = (payload) =>
  AxiosClient.post("v2/gs-graph/edges", payload);

export const updateManualNode = (documentId, payload) =>
  AxiosClient.put(`v2/gs-graph/nodes/${documentId}`, payload);

export const deleteManualNode = (documentId) =>
  AxiosClient.delete(`v2/gs-graph/nodes/${documentId}`);

export const deleteManualEdge = (documentId) =>
  AxiosClient.delete(`v2/gs-graph/edges/${documentId}`);

export const restoreDocs = (docs) =>
  AxiosClient.post("v2/gs-graph/restore", { docs });

// §40.5 phase 2: embedding-space 2D projection of the rendered set.
export const getTopicCoords = (entities) =>
  // 30s belt: a hung server can never pin the "Projecting topics…"
  // spinner forever — timeout rejects, the shell clears pending.
  AxiosClient.post("v2/gs-graph/topic-coords", { entities }, { timeout: 30000 });

// §50.6: what a cited doc id MEANS — {documentId, title, preview, entities}.
// ONE batch round trip for a whole turn, pulled just in time; the graph has
// already reacted on doc ids by the time this lands, so it may fail freely.
export const getDocContext = (docIds) =>
  AxiosClient.post("v2/gs-graph/doc-context", { doc_ids: docIds }, { timeout: 12000 });

// §45C: fetch off-render entities the conversation touched.
export const storyHydrate = (names, filters) =>
  AxiosClient.post("v2/gs-graph/story-hydrate", { names, filters }, { timeout: 15000 });

// SAVE-ON-CLEAR (GRAPH_CONV_TO_THREAD_PLAN): a cleared graph conversation
// becomes a /threads chat thread. This is the graph lane CALLING the threads
// endpoint — it lives here, not in threadsChat.js, because that module
// belongs to the /threads page (its own header enforces the lane split).
// → { thread, turns_imported, clipped } — clipped=true when the server kept
// only the newest turns; the caller's toast must say so (no silent caps).
export const importGraphThread = (turns) =>
  AxiosClient.post("v2/chat/threads/import", { turns }, { timeout: 15000 })
    .then((res) => res?.data);

// ── the graph's chat stream ───────────────────────────────────────────────────
// POST /api/v2/chat/ask as NDJSON over fetch (axios cannot stream), same reader
// as `api/v2Chat.js`. It exists SEPARATELY because two §52 features cannot ride
// that one and it belongs to another owner:
//   · §52.4 D — the structured `brief` is a TOP-LEVEL field. `sanitize_context`
//     returns a fixed dict of known keys, so a brief nested in `context` is
//     discarded before anything can look for it and brief mode never arms.
//     `askGraphChat` serializes exactly {question, context, history}.
//   · §52.2 B3 — `status` events. `askGraphChat` dispatches five types with no
//     else branch, so a status line is parsed and thrown away.
// SEAM (owner of ui/src/api/v2Chat.js): this is a fork of that reader and wants
// to be folded back into it — add `brief` to the body and an `onStatus`
// handler, then delete this and re-point the page's import. Nothing else in the
// app imports either one.
//
// The MIMETYPE IS LOAD-BEARING on the server side (application/x-ndjson is why
// gzip skips the route and status events flush at all); nothing here may ask
// for another one.
export const askGraphChatStream = (
  { question, context, history, brief },
  { onBrief, onCitations, onStatus, onToken, onAction, onDone, onError },
) => {
  const controller = new AbortController();
  const user = localStorage.getItem("user");

  (async () => {
    try {
      const res = await fetch(BASE_URL + "v2/chat/ask", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-access-token": user,
        },
        // An ordinary ask sends no `brief` key at all, so the wire is byte-for
        // byte what it was before this mode existed.
        body: JSON.stringify(
          brief ? { question, context, history, brief } : { question, context, history },
        ),
        signal: controller.signal,
      });
      if (!res.ok) {
        let message = `request failed (${res.status})`;
        try {
          message = (await res.json()).error || message;
        } catch {
          /* non-JSON error body */
        }
        onError?.(message);
        return;
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let carry = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        carry += decoder.decode(value, { stream: true });
        const lines = carry.split("\n");
        carry = lines.pop(); // last element is a partial line (or "")
        for (const line of lines) {
          if (!line.trim()) continue;
          let event;
          try {
            event = JSON.parse(line);
          } catch {
            continue; // malformed line: skip, keep the stream
          }
          if (event.type === "citations") onCitations?.(event);
          else if (event.type === "brief") onBrief?.(event);
          else if (event.type === "status") onStatus?.(event);
          else if (event.type === "action") onAction?.(event);
          else if (event.type === "token") onToken?.(event.text);
          else if (event.type === "done") onDone?.(event);
          else if (event.type === "error") onError?.(event.message);
          // Unknown types are dropped, deliberately: that is what lets the
          // server ship new events first.
        }
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        onError?.(err?.message || "stream failed");
      }
    }
  })();

  return () => controller.abort();
};
